import * as yup from 'yup';
import {
  withScope as sentryWithScope,
  Scope,
  captureException,
} from '@sentry/browser';

import { QuickSendProgressData } from 'components/pages/quickSend/QuickSendProgressPage';

const QS_KEY_PREFIX = 'qs_' as const;

const documentSchema = yup.object().shape({
  id: yup.string().required(),
  type: yup
    .string()
    .oneOf(['course', 'bundle', 'topic', 'chapter', 'question', 'passage'])
    .required(),
  parentId: yup.string().notRequired(),
});

const quickSendProgressDataSchema = yup.object().shape({
  courseId: yup.string().required(),
  organizationId: yup
    .string()
    .nullable()
    .default(null),
  documents: yup
    .array()
    .of(documentSchema)
    .default([]),
  documentsForTracking: yup
    .array()
    .of(documentSchema)
    .default([]),
  id: yup.string().required(),
  name: yup.string().required(),
  iconUrl: yup
    .string()
    .default('')
    .test('is-valid-url', 'Invalid URL protocol', value => {
      if (!value || value === '') return true;
      try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    }),
  resourceType: yup
    .string()
    .oneOf(['courses', 'bundles', 'topics'])
    .required(),
});

export const generateQuickSendKey = (): string => {
  return `${QS_KEY_PREFIX}${Date.now()}`;
};

export const isValidQuickSendKey = (key: string): boolean => {
  return key.startsWith(QS_KEY_PREFIX) && /^qs_\d+$/.test(key);
};

export const setQuickSendData = (
  data: QuickSendProgressData,
): string | null => {
  const key = generateQuickSendKey();
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return key;
  } catch (error) {
    sentryWithScope((scope: Scope) => {
      scope.setExtras({ key });
      captureException(error);
    });
    return null;
  }
};

export const getQuickSendData = (key: string): QuickSendProgressData | null => {
  if (!isValidQuickSendKey(key)) return null;

  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return quickSendProgressDataSchema.validateSync(parsed, {
      abortEarly: false,
      strict: false,
    }) as QuickSendProgressData;
  } catch (error) {
    console.error('Failed to validate QuickSendProgressData:', error);

    sentryWithScope((scope: Scope) => {
      scope.setExtras({ raw, key });
      captureException(error);
    });

    return null;
  }
};
